import type { MachineEventRecord, ScheduleSlotRecord } from '../types/records'
import { slotDurationHours } from './scheduleCalculations'
import { totalDowntimeHours } from './machineEvents'

export type OeeMetrics = {
  availability: number
  performance: number
  quality: number
  oee: number
}

function ratioPercent(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0
  return Math.round(Math.min(1, Math.max(0, ratio)) * 1000) / 10
}

export function computeOee(params: {
  availableHours: number
  downtimeHours: number
  plannedOutput: number
  actualOutput: number
  defectQty: number
}): OeeMetrics {
  const available = Math.max(0, Number(params.availableHours) || 0)
  const downtime = Math.max(0, Number(params.downtimeHours) || 0)
  const running = Math.max(0, available - downtime)
  const planned = Math.max(0, Number(params.plannedOutput) || 0)
  const actual = Math.max(0, Number(params.actualOutput) || 0)
  const defect = Math.max(0, Number(params.defectQty) || 0)
  const good = Math.max(0, actual - defect)

  const availability = available > 0 ? running / available : 0
  const performance = planned > 0 ? actual / planned : 0
  const quality = actual > 0 ? good / actual : 0

  return {
    availability: ratioPercent(availability),
    performance: ratioPercent(performance),
    quality: ratioPercent(quality),
    oee: ratioPercent(availability * performance * quality),
  }
}

export function machineUtilizationPercent(
  slots: ScheduleSlotRecord[],
  availableHours: number,
): number {
  const available = Math.max(0, Number(availableHours) || 0)
  if (available <= 0) return 0

  let scheduled = 0
  for (const slot of slots) {
    if (slot.status === 'cancelled') continue
    scheduled += slotDurationHours(slot.plannedStart, slot.plannedEnd)
  }

  return ratioPercent(scheduled / available)
}

export function aggregateSlotOutput(slots: ScheduleSlotRecord[]): {
  plannedOutput: number
  actualOutput: number
  defectQty: number
  scheduledHours: number
} {
  let plannedOutput = 0
  let actualOutput = 0
  let defectQty = 0
  let scheduledHours = 0

  for (const slot of slots) {
    if (slot.status === 'cancelled') continue
    plannedOutput += Number(slot.plannedQuantity) || 0
    actualOutput += Number(slot.actualQuantity) || 0
    defectQty += Number(slot.defectQuantity) || 0
    scheduledHours += slotDurationHours(slot.plannedStart, slot.plannedEnd)
  }

  return {
    plannedOutput,
    actualOutput,
    defectQty,
    scheduledHours: Math.round(scheduledHours * 100) / 100,
  }
}

export function machineOeeForPeriod(
  slots: ScheduleSlotRecord[],
  events: MachineEventRecord[],
  availableHours: number,
  rangeStart: string,
  rangeEnd: string,
): OeeMetrics {
  const { plannedOutput, actualOutput, defectQty } = aggregateSlotOutput(slots)
  const downtimeHours = totalDowntimeHours(events, rangeStart, rangeEnd)

  return computeOee({
    availableHours,
    downtimeHours,
    plannedOutput,
    actualOutput,
    defectQty,
  })
}

export function averageOutputDeviationPercent(slots: ScheduleSlotRecord[]): number | null {
  const active = slots.filter((s) => s.status !== 'cancelled' && s.actualQuantity != null)
  if (active.length === 0) return null

  let sum = 0
  let count = 0
  for (const slot of active) {
    const planned = Number(slot.plannedQuantity) || 0
    if (planned <= 0) continue
    sum += ((Number(slot.actualQuantity) - planned) / planned) * 100
    count += 1
  }
  if (count === 0) return null
  return Math.round((sum / count) * 10) / 10
}
